import type {
  AddressSummaryResponse,
  AddressTransactionsResponse,
  AddressTokensResponse,
  TxV1Response,
  WalletLeaderboard,
  TokenLeaderboardResponse,
  NetworkStats,
  PeriodTpvStats,
  TokenDetailsResponse,
  TokenHoldersResponse,
} from '@sparkscan/api-react-sdk-hooks';

// // Import the SDK hooks
import {
  useAddressSummaryV1AddressAddressGetHook,
  useGetAddressTransactionsV1AddressAddressTransactionsGetHook,
  useGetAddressTokensV1AddressAddressTokensGetHook,
  useGetLatestTransactionsV1TxLatestGetHook,
  useGetTransactionDetailsByIdV1TxTxidGetHook,
  useGetWalletLeaderboardV1StatsLeaderboardWalletsGetHook,
  useGetTokenLeaderboardV1StatsLeaderboardTokensGetHook,
  useGetNetworkStatsV1StatsSummaryGetHook,
  useGetTpvStatsV1StatsTpvGetHook,
  useGetTokenInfoByIdentifierV1TokensIdentifierGetHook,
  useGetTokenTransactionsV1TokensPubkeyTransactionsGetHook,
  useGetTokenHoldersV1TokensPubkeyHoldersGetHook,
  useGetHistoricalActiveWalletsV1StatsHistoricalActiveWalletsGetHook,
  useGetHistoricalTvlV1StatsHistoricalTvlGetHook,
  useGetHistoricalTpvV1StatsHistoricalTpvGetHook,
} from '@sparkscan/api-react-sdk-hooks';

import type { TpvPeriod } from '@sparkscan/api-react-sdk-hooks';
import { useState, useEffect } from 'react';

// // Re-export all response types
export type {
  AddressSummaryResponse,
  AddressTransactionsResponse,
  AddressTokensResponse,
  TxV1Response as TxLatestResponse,
  TxV1Response as TxIdResponse,
  WalletLeaderboard as WalletLeaderboardResponse,
  TokenLeaderboardResponse,
  NetworkStats as NetworkStatsResponse,
  PeriodTpvStats as TpvStatsResponse,
  TokenDetailsResponse,
  TokenHoldersResponse,
};

export const API_BASE = process.env.EQUALEYES_API || 'https://api.sparkscan.io';

// Re-export the hooks with the same names and signatures as before
export function useAddressSummary(address: string, params: { network: 'REGTEST' | 'MAINNET' }) {
  return useAddressSummaryV1AddressAddressGetHook(
    { address },
    { network: params.network },
    { client: { baseURL: API_BASE } }
  );
}

export function useAddressTransactions(
  address: string,
  params: { network: 'REGTEST' | 'MAINNET'; limit?: number; offset?: number }
) {
  return useGetAddressTransactionsV1AddressAddressTransactionsGetHook(
    { address },
    { network: params.network, limit: params.limit, offset: params.offset },
    { client: { baseURL: API_BASE } }
  );
}

export function useAddressTokens(address: string, params: { network: 'REGTEST' | 'MAINNET' }) {
  return useGetAddressTokensV1AddressAddressTokensGetHook(
    { address },
    { network: params.network },
    { client: { baseURL: API_BASE } }
  );
}

export function useTxLatest(params: {
  network: 'REGTEST' | 'MAINNET';
  limit?: number;
  offset?: number;
}) {
  return useGetLatestTransactionsV1TxLatestGetHook(
    {
      network: params.network,
      limit: params.limit,
      offset: params.offset,
    },
    {
      client: { baseURL: API_BASE },
      query: {
        refetchInterval: 1000,
      },
    }
  );
}

export function useTxId(txid: string, params: { network: 'REGTEST' | 'MAINNET' }) {
  return useGetTransactionDetailsByIdV1TxTxidGetHook(
    { txid },
    { network: params.network },
    { client: { baseURL: API_BASE } }
  );
}

export function useStatsLeaderboardWallets(params: {
  network: 'REGTEST' | 'MAINNET';
  limit?: number;
}) {
  return useGetWalletLeaderboardV1StatsLeaderboardWalletsGetHook(
    {
      network: params.network,
      limit: params.limit,
    },
    { client: { baseURL: API_BASE } }
  );
}

export function useStatsLeaderboardTokens(params: {
  network: 'REGTEST' | 'MAINNET';
  limit?: number;
  offset?: number;
}) {
  return useGetTokenLeaderboardV1StatsLeaderboardTokensGetHook(
    {
      network: params.network,
      limit: params.limit,
      offset: params.offset,
    },
    { client: { baseURL: API_BASE } }
  );
}

export function useStatsSummary(params: { network: 'REGTEST' | 'MAINNET' }) {
  return useGetNetworkStatsV1StatsSummaryGetHook(
    { network: params.network },
    { client: { baseURL: API_BASE } }
  );
}

export function useStatsTPV(params: {
  network: 'REGTEST' | 'MAINNET';
  period?: TpvPeriod;
}) {
  return useGetTpvStatsV1StatsTpvGetHook(
    {
      network: params.network,
      period: params.period,
    },
    { client: { baseURL: API_BASE } }
  );
}

export function useToken(identifier: string, params: { network: 'REGTEST' | 'MAINNET' }) {
  return useGetTokenInfoByIdentifierV1TokensIdentifierGetHook(
    { identifier },
    { network: params.network },
    { client: { baseURL: API_BASE } }
  );
}

export function useTokenTransactions(
  pubkey: string,
  params: { network: 'REGTEST' | 'MAINNET'; limit?: number; offset?: number }
) {
  return useGetTokenTransactionsV1TokensPubkeyTransactionsGetHook(
    { pubkey },
    { network: params.network, limit: params.limit, offset: params.offset },
    { client: { baseURL: API_BASE } }
  );
}

export function useTokenHolders(
  pubkey: string,
  params: { network: 'REGTEST' | 'MAINNET'; limit?: number; offset?: number }
) {
  return useGetTokenHoldersV1TokensPubkeyHoldersGetHook(
    { pubkey },
    { network: params.network, limit: params.limit, offset: params.offset },
    { client: { baseURL: API_BASE } }
  );
}

export function useHistoricalActiveWallets(params: {
  network: 'REGTEST' | 'MAINNET';
  startDate: string;
  endDate: string;
  granularity: 'daily' | 'weekly';
}) {
  return useGetHistoricalActiveWalletsV1StatsHistoricalActiveWalletsGetHook(
    {
      network: params.network,
      start_date: params.startDate,
      end_date: params.endDate,
      granularity: params.granularity,
    },
    { client: { baseURL: API_BASE } }
  );
}

export function useHistoricalTvl(params: {
  network: 'REGTEST' | 'MAINNET';
  startDate: string;
  endDate: string;
  granularity: 'daily' | 'weekly';
}) {
  return useGetHistoricalTvlV1StatsHistoricalTvlGetHook(
    {
      network: params.network,
      start_date: params.startDate,
      end_date: params.endDate,
      granularity: params.granularity,
    },
    { client: { baseURL: API_BASE } }
  );
}

export function useHistoricalTpv(params: {
  network: 'REGTEST' | 'MAINNET';
  startDate: string;
  endDate: string;
  granularity: 'daily' | 'weekly';
}) {
  return useGetHistoricalTpvV1StatsHistoricalTpvGetHook(
    {
      network: params.network,
      start_date: params.startDate,
      end_date: params.endDate,
      granularity: params.granularity,
    },
    { client: { baseURL: API_BASE } }
  );
}

// Custom hooks for internal analytics endpoints
export function useNetworkEffectCorrelation(params: {
  network: 'REGTEST' | 'MAINNET';
  days?: number;
}) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/internal/analytics/network-effect`);
        url.searchParams.append('network', params.network);
        if (params.days) {
          url.searchParams.append('days', params.days.toString());
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [params.network, params.days]);

  return { data, isLoading, error };
}

export function useTransactionSuccessRates(params: {
  network: 'REGTEST' | 'MAINNET';
  days?: number;
}) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/internal/analytics/success-rates`);
        url.searchParams.append('network', params.network);
        if (params.days) {
          url.searchParams.append('days', params.days.toString());
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [params.network, params.days]);

  return { data, isLoading, error };
}

export function useTokenConcentration(params: {
  network: 'REGTEST' | 'MAINNET';
  limit?: number;
}) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/internal/analytics/token-concentration`);
        url.searchParams.append('network', params.network);
        if (params.limit) {
          url.searchParams.append('limit', params.limit.toString());
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [params.network, params.limit]);

  return { data, isLoading, error };
}

export function useWalletAgeDistribution(params: {
  network: 'REGTEST' | 'MAINNET';
}) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/internal/analytics/wallet-ages`);
        url.searchParams.append('network', params.network);

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [params.network]);

  return { data, isLoading, error };
}

export function usePaymentMethodUsage(params: {
  network: 'REGTEST' | 'MAINNET';
  days?: number;
}) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/internal/analytics/payment-methods`);
        url.searchParams.append('network', params.network);
        if (params.days) {
          url.searchParams.append('days', params.days.toString());
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [params.network, params.days]);

  return { data, isLoading, error };
}

export function useNetworkFlowAnalysis(params: {
  network: 'REGTEST' | 'MAINNET';
  days?: number;
}) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = new URL(`${API_BASE}/internal/analytics/network-flows`);
        url.searchParams.append('network', params.network);
        if (params.days) {
          url.searchParams.append('days', params.days.toString());
        }

        const response = await fetch(url.toString());
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [params.network, params.days]);

  return { data, isLoading, error };
}
